import { ContractorType } from '@insurance/api/requests'
import { ConditionalText, DateTimeCell, Text } from '@shared/components'
import { DEFAULT_CURRENCY } from '@shared/constants'
import { getFormatCurrency, getFullName } from '@shared/utils'
import { Divider, Flex, Form } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { DescriptionsProps } from 'antd/lib'
import { useTranslation } from 'react-i18next'

import { DetailsWrapper } from '../DetailsWrapper'
import { usePropertyInsuranceValues } from './PropertyInsuranceContext'
import { useStyles } from './styles'
import { PropertyFormValues } from './type'
import { formProps } from './utils'

type ExtraDescriptions = (Required<DescriptionsProps>['items'][number] & {
  hidden?: boolean
})[]

export const Details = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { isEditable, order } = usePropertyInsuranceValues()

  const {
    priceKey,
    contractorProps,
    customerProps,
    validFromProps,
    costProps,
    insuredAmountProps,
    itemsKey,
    ItemCategoryProps,
    ItemDescriptionProps,
    periodProps,
    installmentsProps,
    effectiveDateProps
  } = formProps(t)

  const price = Form.useWatch(priceKey.name, { preserve: true })
  const contractor = Form.useWatch<PropertyFormValues['contractor']>(
    contractorProps.name,
    {
      preserve: true
    }
  )
  const customer = Form.useWatch<PropertyFormValues['customer']>(
    customerProps.name,
    {
      preserve: true
    }
  )
  const validFrom = Form.useWatch(validFromProps.name, {
    preserve: true
  })
  const period = Form.useWatch(periodProps.name, {
    preserve: true
  })
  const installments = Form.useWatch(installmentsProps.name, {
    preserve: true
  })
  const formItems = Form.useWatch(itemsKey.name, {
    preserve: true
  })
  const effectiveDate = Form.useWatch(effectiveDateProps.name, {
    preserve: true
  })

  const isLegalEntity = contractor?.content_type === ContractorType.LEGALENTITY

  const mainItems: ExtraDescriptions = [
    {
      label: contractorProps.label,
      children: (
        <ConditionalText
          condition={!!contractor?.content_type}
          className={styles.text}
        >
          {t(contractor?.content_type)}
        </ConditionalText>
      )
    },
    {
      label: t('company_name'),
      children: (
        <ConditionalText
          condition={!!contractor?.legal_entity?.name}
          className={styles.text}
        >
          {contractor?.legal_entity?.name}
        </ConditionalText>
      ),
      hidden: !isLegalEntity
    },
    {
      label: t('client_name'),
      children: (
        <ConditionalText
          condition={!!getFullName(contractor?.person)}
          className={styles.text}
        >
          {getFullName(contractor?.person)}
        </ConditionalText>
      ),
      hidden: isLegalEntity
    },
    {
      label: customerProps.label,
      children: (
        <ConditionalText
          condition={!!getFullName(customer)}
          className={styles.text}
        >
          {getFullName(customer)}
        </ConditionalText>
      )
    },
    {
      label: validFromProps.label,
      children: (
        <DateTimeCell
          date={validFrom}
          hiddenTime
          dateProps={{ className: styles?.date }}
        />
      ),
      hidden: isEditable
    },
    {
      label: effectiveDateProps.label,
      children: (
        <DateTimeCell
          date={effectiveDate}
          hiddenTime
          dateProps={{ className: styles?.date }}
        />
      ),
      hidden: !isEditable
    },
    {
      label: periodProps.label,
      children: (
        <ConditionalText condition={!!period} className={styles.text}>
          {t(`m${period}`)}
        </ConditionalText>
      )
    },
    {
      label: installmentsProps.label,
      children: (
        <ConditionalText condition={!!installments} className={styles.text}>
          {t(`installment_${installments}`)}
        </ConditionalText>
      )
    }
  ]

  const itemsDescriptions: ExtraDescriptions =
    formItems?.map((item: AnyObject, index: number) => {
      const name = item?.[ItemDescriptionProps?.name]
      const category = item?.[ItemCategoryProps?.name]
      const cost = item?.[costProps?.name]
      const amount = item?.[insuredAmountProps?.name]

      return {
        label: t('item_number', { number: index + 1, name: name || '' }),
        children: (
          <Flex vertical gap={4}>
            {category && (
              <ConditionalText condition={!!category} className={styles.text}>
                {t('category')}: {t(`item_category_${category}`)}
              </ConditionalText>
            )}

            {cost && (
              <ConditionalText condition={!!cost} className={styles.text}>
                {costProps.label}:{' '}
                {getFormatCurrency({
                  amount: cost,
                  currency: DEFAULT_CURRENCY
                })}
              </ConditionalText>
            )}

            {amount && (
              <ConditionalText condition={!!amount} className={styles.text}>
                {insuredAmountProps.label}:{' '}
                {getFormatCurrency({
                  amount: amount,
                  currency: DEFAULT_CURRENCY
                })}
              </ConditionalText>
            )}
          </Flex>
        )
      }
    }) || []

  const items = [...mainItems, ...itemsDescriptions].filter(
    ({ hidden }) => !hidden
  )

  const orderPrice = {
    amount: order?.full_price ? Number(order.full_price) : 0,
    currency: order?.currency
  }

  const isMoreThanOrderPrice = price?.amount - orderPrice?.amount > 0

  return (
    <DetailsWrapper items={items}>
      {price && !isEditable && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong size={18}>
              {t('total_to_be_paid')}
            </Text>
            <Text strong size={18} className="text-wrap-nowrap">
              {getFormatCurrency(price)}
            </Text>
          </Flex>

          <Divider />
        </>
      )}

      {isEditable && (
        <>
          {orderPrice && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('policy_amount_paid')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(orderPrice)}
              </Text>
            </Flex>
          )}
          {price && (
            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('new_amount')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency(price)}
              </Text>
            </Flex>
          )}
          {orderPrice && price && (
            <Flex align="center" justify="space-between">
              <Text
                strong
                size={22}
                type={isMoreThanOrderPrice ? 'success' : 'danger'}
              >
                {isMoreThanOrderPrice
                  ? t('amount_to_be_paid')
                  : t('amount_to_be_returned')}
              </Text>
              <Text
                strong
                size={22}
                type={isMoreThanOrderPrice ? 'success' : 'danger'}
                className="text-wrap-nowrap"
              >
                {getFormatCurrency({
                  amount: price.amount - orderPrice.amount,
                  currency: price.currency
                })}
              </Text>
            </Flex>
          )}

          <Divider />
        </>
      )}
    </DetailsWrapper>
  )
}
